import { useEffect, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, FormField } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useSellDayPass } from "@/api/hooks/useMemberships";
import { ContactPicker } from "./ContactPicker";

interface SellDayPassDialogProps {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
  onSold?: () => void;
}

/** Sells a $30 Hummingbird Day Pass. The server enforces the owner's
 * capacity throttle rule (409 when the day's remaining capacity is
 * exhausted) — surfaced verbatim below, never re-derived client-side. */
export function SellDayPassDialog({ open, onClose, defaultDate, onSold }: SellDayPassDialogProps) {
  const [contactId, setContactId] = useState("");
  const [passDate, setPassDate] = useState(defaultDate ?? "");
  const [serverError, setServerError] = useState<string | null>(null);

  const sellDayPass = useSellDayPass();

  useEffect(() => {
    if (open) {
      setContactId("");
      setPassDate(defaultDate ?? "");
      setServerError(null);
    }
  }, [open, defaultDate]);

  const canSubmit = !!contactId && !!passDate && !sellDayPass.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    setServerError(null);
    try {
      await sellDayPass.mutateAsync({ contact_id: contactId, pass_date: passDate });
      toastSuccess("Day pass sold");
      onSold?.();
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't sell day pass", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Sell day pass">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Sell Hummingbird Day Pass — $30</DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Contact</label>
            <ContactPicker value={contactId} onChange={(id) => setContactId(id)} />
          </div>
          <FormField label="Date" type="date" value={passDate} onChange={(e) => setPassDate(e.target.value)} />
          {serverError && (
            <p className="text-sm text-danger" role="alert" data-testid="sell-day-pass-error">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={sellDayPass.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-sell-day-pass">
            {sellDayPass.isPending ? "Selling…" : "Sell day pass"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
